import RedWorldDark from "../Logo/RedWorldDark";
import { useNavigate, useLocation } from "react-router-dom";
import FacebookCircled from "../../Atomos/Icons/ViewMode/Facebook";
import XCircled from "../../Atomos/Icons/XCircled";
import Linkedin from "../../Atomos/Icons/ViewMode/Linkedin";
import { useDispatch, useSelector } from "react-redux";
import { setSideMenuHeight } from "../../../redux";
import { Appstore } from "../../../redux";
import { useEffect, useRef } from "react";

const links = [
  { name: "HOME", to: "/" },
  { name: "SITE MAP", to: "/sitemap.asp" },
  { name: "PRIVACY", to: "/privacypolicy.asp" },
  { name: "DISCLAIMER", to: "/disclaimer.asp" },
  { name: "CONTACT", to: "/contacts.asp" },
];

const socialMedia = [
  {
    name: "Facebook",
    icon: <FacebookCircled />,
    to: "https://www.facebook.com/worldservicesgroup",
  },
  {
    name: "X",
    icon: <XCircled className="fill-current" />,
    to: "https://twitter.com/worldservsgroup",
  },
  {
    name: "LinkedIn",
    icon: <Linkedin />,
    to: "https://www.linkedin.com/company/worldservicesgroup",
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const footerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const isLoading = useSelector((state: Appstore) => state.loader.isLoading);
  const headerHeight = useSelector((state: Appstore) => state.menus.headerHeight);

  useEffect(() => {
    let ticking = false;
    let lastHeight = 0;

    function handleScrollOrResize(source: string = "unknown") {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const footer = footerRef.current;
          if (footer) {
            const rect = footer.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            let newHeight;

            if (rect.top >= windowHeight) {
              newHeight = windowHeight - headerHeight;
            } else if (rect.top < windowHeight && rect.top > 0) {
              const availableHeight = rect.top - headerHeight;
              newHeight = Math.max(200, availableHeight - 10);
            } else {
              newHeight = windowHeight - headerHeight;
            }

            if (Math.abs(newHeight - lastHeight) > 5) {
              lastHeight = newHeight;
              dispatch(setSideMenuHeight(newHeight));
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    const handleWindowScroll = () => handleScrollOrResize("window");
    const handleDocumentScroll = () => handleScrollOrResize("document");
    const handleBodyScroll = () => handleScrollOrResize("body");
    const handleContentScroll = () => handleScrollOrResize("content-container");
    const handleResize = () => handleScrollOrResize("resize");

    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    document.addEventListener("scroll", handleDocumentScroll, {
      passive: true,
    });
    document.body.addEventListener("scroll", handleBodyScroll, {
      passive: true,
    });
    window.addEventListener("resize", handleResize);

    let contentContainer: HTMLElement | null = null;
    const setupContentContainer = () => {
      contentContainer = document.getElementById("content-container");
      if (contentContainer) {
        contentContainer.addEventListener("scroll", handleContentScroll, {
          passive: true,
        });
      }
    };

    setupContentContainer();
    const containerSetupTimeout = setTimeout(setupContentContainer, 100);

    const timeoutId = setTimeout(
      () => handleScrollOrResize("initial-load"),
      300
    );

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(containerSetupTimeout);
      window.removeEventListener("scroll", handleWindowScroll);
      document.removeEventListener("scroll", handleDocumentScroll);
      document.body.removeEventListener("scroll", handleBodyScroll);
      window.removeEventListener("resize", handleResize);
      if (contentContainer) {
        contentContainer.removeEventListener("scroll", handleContentScroll);
      }
    };
  }, [dispatch, location.pathname]);

  return (
    <div
      id="main-footer"
      ref={footerRef}
      className="bg-white flex w-full items-center px-4 py-6 md:px-8 md:py-8 lg:px-12 lg:py-12 lg:flex-row flex-col relative"
      style={{
        display: isLoading ? "none" : "flex",
        pointerEvents: "auto",
        zIndex: 0,
      }}
    >
      <div className="flex h-full w-24 md:w-32 lg:w-40 items-center mb-4 lg:mb-0">
        <RedWorldDark />
      </div>

      <div className="flex flex-col flex-1 mx-4 md:mx-6 lg:mx-10 gap-2 md:gap-3 lg:gap-4 mb-4 lg:mb-0">
        <div className="flex flex-row justify-center gap-2 md:gap-4 lg:gap-6 flex-wrap">
          {links.map((l, idx) => (
            <button
              key={l.name + idx}
              className="text-primary text-xs md:text-sm lg:text-[15px] font-decimal font-semibold"
              onClick={() => {
                if (l.to) {
                  navigate(l.to);
                }
              }}
              type="button"
            >
              {l.name}
            </button>
          ))}
        </div>
        <div className="text-xs md:text-sm lg:text-[15px] text-gray-font text-center">
          <p>
            WSG's members are independent firms and are not affiliated in the
            joint practice of professional services.
          </p>
          <p>
            Each member exercises its own individual judgments on all client
            matters.
          </p>
        </div>
        <div className="text-xs md:text-sm lg:text-[15px] text-gray-font text-center mb-4 lg:mb-0">
          <p>© {new Date().getFullYear()}, World Services Group</p>
        </div>
      </div>

      <div className="flex gap-2 md:gap-3 lg:gap-4 mb-4 lg:mb-0">
        {socialMedia.map((s) => (
          <a
            key={s.name}
            href={s.to}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white bg-primary rounded-full"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 p-2 md:p-2.5 lg:p-3">
              {s.icon}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
